import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, TextFormat, getSortState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './critical-logging.reducer';

export const CriticalLogging = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search)
  );

  const criticalLoggingList = useAppSelector(state => state.criticalLogging.entities);
  const loading = useAppSelector(state => state.criticalLogging.loading);
  const totalItems = useAppSelector(state => state.criticalLogging.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="critical-logging-heading" data-cy="CriticalLoggingHeading">
        <Translate contentKey="activitiesApp.criticalLogging.home.title">Critical Loggings</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="activitiesApp.criticalLogging.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/critical-logging/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="activitiesApp.criticalLogging.home.createLabel">Create new Critical Logging</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {criticalLoggingList && criticalLoggingList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="activitiesApp.criticalLogging.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('createdDate')}>
                  <Translate contentKey="activitiesApp.criticalLogging.createdDate">Created Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdDate')} />
                </th>
                <th className="hand" onClick={sort('logRef')}>
                  <Translate contentKey="activitiesApp.criticalLogging.logRef">Log Ref</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('logRef')} />
                </th>
                <th className="hand" onClick={sort('logLevel')}>
                  <Translate contentKey="activitiesApp.criticalLogging.logLevel">Log Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('logLevel')} />
                </th>
                <th className="hand" onClick={sort('logMessage')}>
                  <Translate contentKey="activitiesApp.criticalLogging.logMessage">Log Message</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('logMessage')} />
                </th>
                <th className="hand" onClick={sort('otherRef')}>
                  <Translate contentKey="activitiesApp.criticalLogging.otherRef">Other Ref</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('otherRef')} />
                </th>
                <th className="hand" onClick={sort('sesionId')}>
                  <Translate contentKey="activitiesApp.criticalLogging.sesionId">Sesion Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sesionId')} />
                </th>
                <th className="hand" onClick={sort('userId')}>
                  <Translate contentKey="activitiesApp.criticalLogging.userId">User Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('userId')} />
                </th>
                <th className="hand" onClick={sort('ipAddress')}>
                  <Translate contentKey="activitiesApp.criticalLogging.ipAddress">Ip Address</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ipAddress')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {criticalLoggingList.map((criticalLogging, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/critical-logging/${criticalLogging.id}`} color="link" size="sm">
                      {criticalLogging.id}
                    </Button>
                  </td>
                  <td>
                    {criticalLogging.createdDate ? (
                      <TextFormat type="date" value={criticalLogging.createdDate} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{criticalLogging.logRef}</td>
                  <td>{criticalLogging.logLevel}</td>
                  <td>{criticalLogging.logMessage}</td>
                  <td>{criticalLogging.otherRef}</td>
                  <td>{criticalLogging.sesionId}</td>
                  <td>{criticalLogging.userId}</td>
                  <td>{criticalLogging.ipAddress}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/critical-logging/${criticalLogging.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/critical-logging/${criticalLogging.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/critical-logging/${criticalLogging.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="activitiesApp.criticalLogging.home.notFound">No Critical Loggings found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={criticalLoggingList && criticalLoggingList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default CriticalLogging;
